import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Spin, Button, Modal as AntModal, message as $message } from 'antd'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'

import Cropper from 'react-cropper'
import { transImageToSursung } from '@/utils/file'
import ajax from './ajax'

import styles from './Cropper.less'

const CROPPER_WIDTH_DEFAULT = 800
const CROPPER_AREA_WIDTH = 350
const CROPPER_AREA_HEIGHT = 600
const SELECT_AREA_WIDTH = 300
const ENCODER_OPTION = 0.8

export const CROPPER_SIZE_DEFAULT = {
    label: '1:1',
    value: 1
}

// 自由比例
export const CROPPER_SIZE_FREEDOM = {
    label: '自由比例',
    value: NaN
}

const transDataURLType = imgType => {
    if (imgType) {
        if (imgType === 'jpg' || imgType === 'jpeg') {
            return 'jpeg'
        } else {
            return imgType
        }
    }
    return 'jpeg'
}

const convertBase64UrlToBlob = ({ dataURL, imgType = 'image/png' }) => {
    let bytes = window.atob(dataURL.split(',')[1])

    // 处理异常,将ascii码小于0的转换为大于0
    let ab = new ArrayBuffer(bytes.length)
    let ia = new Uint8Array(ab)
    for (let i = 0; i < bytes.length; i++) {
        ia[i] = bytes.charCodeAt(i)
    }
    return new Blob([ab], { type: `image/${imgType}` })
}

export default ({ dataSource, fileDirectory, isTranscoder, visible, onOk, onCancel }) => {
    const { cropRecSizes } = dataSource // 推荐尺寸
    const [activeIndex, setActiveIndex] = useState(0)
    const [loading, setLoading] = useState(true)
    const [confirmLoading, setConfirmLoading] = useState(false)
    const [src, setSrc] = useState(null)
    const [imgType, setImgType] = useState('jpg')
    const [cropBoxSize, setCropBoxSize] = useState(null)

    const initialAspectRatio = useMemo(() => cropRecSizes?.[0] || CROPPER_SIZE_DEFAULT, [])
    const currCropRecSize = useMemo(() => cropRecSizes?.[activeIndex] || null, [activeIndex, cropRecSizes])

    const aspectRatio = useMemo(
        () => (currCropRecSize && currCropRecSize.fixedRatio ? currCropRecSize : CROPPER_SIZE_FREEDOM),
        [currCropRecSize]
    ) // 图片比例

    const isCheckCoincide = useMemo(() => {
        const { minWidth, maxWidth, minHeight, maxHeight } = dataSource || {}
        let isNeed = true
        if (
            typeof minWidth === 'undefined' &&
            typeof maxWidth === 'undefined' &&
            typeof minHeight === 'undefined' &&
            typeof maxHeight === 'undefined'
        ) {
            isNeed = false
        }
        return isNeed
    }, [])

    const cropperRef = useRef(null)

    useEffect(() => {
        if (visible) {
            setLoading(true)
            const { src } = dataSource
            const isPng = src && src.indexOf('.png') !== -1
            if (isPng) {
                setImgType('png')
            }
            if (src) {
                transImageToSursung([src]).then(([newSrc]) => {
                    setSrc(newSrc)
                })
            }
        } else {
            setSrc(null)
        }
        return () => {
            cropperRef.current = null
        }
    }, [visible])

    // 检测是否符合上传图要求
    const isCoincide = useMemo(() => {
        if (!isCheckCoincide || aspectRatio?.output) {
            return true
        }

        const { w, h } = cropBoxSize || {}
        const { minWidth, maxWidth, minHeight, maxHeight } = dataSource || {}

        if (typeof w === 'undefined' && typeof h === 'undefined') {
            return true
        }

        if (
            typeof minWidth === 'undefined' &&
            typeof maxWidth === 'undefined' &&
            typeof minHeight === 'undefined' &&
            typeof maxHeight === 'undefined'
        ) {
            return true
        }

        if (
            (minWidth && w < minWidth) ||
            (maxWidth && w > maxWidth) ||
            (minHeight && h < minHeight) ||
            (maxHeight && h > maxHeight)
        ) {
            return false
        }

        return true
    }, [isCheckCoincide, cropBoxSize, aspectRatio, dataSource])

    // 初始化裁切框尺寸大小 （可针对不同建议尺寸初始化裁切框大小）
    const handleInitCropSize = useCallback(cropRecSize => {
        const imageElement = cropperRef?.current
        const cropper = imageElement?.cropper

        const { label, value } = cropRecSize ?? {}

        if (value && cropper) {
            const labelSize = label ? label.split('x') : []
            const labelW =
                Array.isArray(labelSize) && labelSize.length ? parseFloat(labelSize[0]) : CROPPER_WIDTH_DEFAULT
            cropper.reset()

            let targetW = SELECT_AREA_WIDTH * parseFloat(value)
            let targetH = 300

            cropper.setAspectRatio(value)
            // 裁切框设置
            cropper.setCropBoxData({
                width: targetW,
                height: targetH,
                top: (CROPPER_AREA_WIDTH - targetH) / 2,
                left: (CROPPER_AREA_HEIGHT - targetW) / 2
            })

            // 背景图设置
            cropper.zoomTo(targetW / labelW)
        }
    }, [])

    // 裁切提交保存
    const handleCropSubmit = async () => {
        setConfirmLoading(true)
        const imageElement = cropperRef?.current
        const cropper = imageElement?.cropper

        if (cropper) {
            const toDataURLType = transDataURLType(imgType)

            let getCanvasParams = { imageSmoothingQuality: 'high' }

            // 透明部分填充白色（默认是黑色）
            if (imgType === 'jpg' || imgType === 'jpeg') {
                getCanvasParams.fillColor = '#fff'
            }

            const dataURL = cropper
                .getCroppedCanvas(getCanvasParams)
                .toDataURL(`image/${toDataURLType}`, ENCODER_OPTION)
            try {
                const { w, h } = cropBoxSize || {}
                let file = convertBase64UrlToBlob({ dataURL, imgType })

                let options = {
                    file: {
                        name: '裁切图片',
                        source: file,
                        width: w,
                        height: h
                    },
                    fileDirectory,
                    isTranscoder,
                    fileSuffix: `.${imgType}`,
                    onProgress: () => {},
                    onSuccess: e => {
                        setConfirmLoading(false)
                        onOk && onOk(e)
                    },
                    onError: e => {
                        setConfirmLoading(false)
                        $message.error('上传裁切图片失败，请稍后再试~', e)
                    }
                }

                // output 控制输出图片是否按配置像素输出
                if (aspectRatio?.output) {
                    const { output } = aspectRatio

                    let Img = new Image()
                    let canvasDataURL = ''
                    Img.src = dataURL
                    Img.onload = async () => {
                        let canvas = document.createElement('canvas') // 创建canvas元素
                        let width = Number(output.width) || Img.width // 确保canvas的尺寸和图片一样
                        let height = Number(output.height) || Img.height
                        canvas.width = width
                        canvas.height = height
                        canvas.getContext('2d').drawImage(Img, 0, 0, width, height) // 将图片绘制到canvas中
                        canvasDataURL = canvas.toDataURL(`image/${imgType}`) // 转换图片为dataURL

                        // 传入宽高
                        options.file.width = width
                        options.file.height = height

                        options.file.source = convertBase64UrlToBlob({ dataURL: canvasDataURL, imgType })
                        await ajax(options)
                    }
                } else {
                    await ajax(options)
                }
            } catch (e) {
                console.error('catchError', e)
                setConfirmLoading(false)
            }
        } else {
            $message.error('获取裁切图片失败，请稍后再试~')
            setConfirmLoading(false)
        }
    }

    return (
        <AntModal
            destroyOnClose
            title="裁剪图片"
            width="980px"
            style={{ top: 'calc((100vh - 580px)/2)' }}
            visible={visible}
            maskClosable={false}
            onCancel={onCancel}
            confirmLoading={confirmLoading}
            footer={
                <>
                    <Button className={styles.cropperSizeBtn} onClick={onCancel}>
                        取消
                    </Button>
                    <Button
                        className={styles.cropperSizeBtn}
                        type="primary"
                        disabled={!isCoincide}
                        onClick={handleCropSubmit}
                    >
                        确定
                    </Button>
                </>
            }
        >
            <div className={styles.cropperContainer}>
                {loading ? (
                    <div className={styles.cropperLoading}>
                        <Spin
                            size="small"
                            spinning={true}
                            indicator={<LoadingOutlined style={{ color: '#999', fontSize: '28px' }} spin />}
                        >
                            <div style={{ height: '427px' }} />
                        </Spin>
                    </div>
                ) : null}
                <div>
                    {Array.isArray(cropRecSizes) && cropRecSizes.length ? (
                        <div className={styles.cropperTitle}>
                            <div>{currCropRecSize && currCropRecSize?.output ? '固定尺寸：' : '推荐尺寸：'}</div>
                            <div className={styles.recCropperSize}>
                                {cropRecSizes.map((v, index) => {
                                    return (
                                        <div
                                            key={`${v.label}_${index}`}
                                            className={`${styles.sizeItem} ${
                                                index === activeIndex ? styles.selected : ''
                                            }`}
                                            onClick={() => {
                                                setActiveIndex(index)
                                                handleInitCropSize(cropRecSizes[index])
                                            }}
                                        >
                                            {v.name || v.label}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ) : null}

                    <div className={styles.cropperBox}>
                        <div className={styles.cropperLeftBox}>
                            <div className={styles.cropperTitle}>裁切区：</div>
                            <div className={styles.cropperContent}>
                                <Cropper
                                    src={src}
                                    style={{ height: 350, width: 600 }}
                                    zoomTo={0.5}
                                    initialAspectRatio={initialAspectRatio}
                                    ref={cropperRef}
                                    dragMode="move"
                                    minCropBoxHeight={10}
                                    minCropBoxWidth={10}
                                    viewMode={0}
                                    zoomable={true}
                                    zoomOnTouch={true}
                                    zoomOnWheel={true}
                                    wheelZoomRatio={0.1}
                                    aspectRatio={aspectRatio.value} // 这个是设置比例的参数 (数据格式为：2 / 3)
                                    guides={false}
                                    preview=".cropperPreview"
                                    crop={event => {
                                        const w = parseInt(event.detail.width, 10)
                                        const h = parseInt(event.detail.height, 10)
                                        if (cropBoxSize && cropBoxSize.w === w && cropBoxSize.h === h) {
                                            return
                                        }
                                        setCropBoxSize({
                                            w,
                                            h
                                        })
                                    }}
                                    ready={() => {
                                        cropRecSizes?.[0] && handleInitCropSize(cropRecSizes[0])
                                        setLoading(false)
                                    }}
                                />
                            </div>
                        </div>
                        <div className={styles.cropperRightBox}>
                            <div className={styles.cropperTitle}>
                                <span>图片预览：</span>
                            </div>
                            <div className={styles.previewContainer}>
                                <div
                                    className="cropperPreview"
                                    style={{ width: '300px', overflow: 'hidden', float: 'left', height: '300px' }}
                                />
                            </div>
                            {cropBoxSize ? (
                                <div className={styles.cropperSizePreview}>
                                    <div className={styles.cropperSizePreviewNum}>
                                        预览图像素: {`${cropBoxSize.w} x ${cropBoxSize.h}`}&emsp;
                                    </div>
                                    {!isCoincide ? (
                                        <div className={styles.cropperSizeErrorTip}>
                                            提示：尺寸不合法（当前尺寸不符合要求）
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </AntModal>
    )
}
